import { useState } from 'react'
import { IconButton, Spinner, Typography } from '@material-tailwind/react'
import { AiOutlinePlus } from 'react-icons/ai'
import { useTranslation } from 'react-i18next'
import ResponsiveDialog from '../components/ResponsiveDialog'
import { useUserExperiences } from '../hooks/useUserExperiences'
import { usePageTitle } from '../hooks/toolkit'
import EditPatternForm from '../components/EditPatternForm'
import Center from '../components/Center'
import { useExperiencesPatterns } from '../hooks/useExperiencesPatterns'
import { useFeatureAuth } from '../hooks/authorization'
import { FEATURES } from '../utils/constants/features.constants'
import Unauthorized from './Unauthorized'
import NoUploads from '../components/NoUploads'
import PatternPreviewCard from '../ViewDashboard/PatternPreviewCard'

const DashboardPatterns = () => {
  const { t } = useTranslation()
  usePageTitle('Patterns | Agami Dashboard')
  const { experienceList, isLoading: expLoading } = useUserExperiences({
    excludeDefaultExperience: true,
  })

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const { patternsList, refetch, isLoading: patternsLoading } = useExperiencesPatterns()
  const isLoading = expLoading || patternsLoading

  const patternsAuth = useFeatureAuth(FEATURES.CREATE_PATTERNS)

  if (!patternsAuth) {
    return <Unauthorized />
  }

  return (
    <>
      <ResponsiveDialog
        isOpen={createDialogOpen}
        setIsOpen={setCreateDialogOpen}
        title={t('dashboard.createPattern')}
      >
        {isLoading ? (
          <Center>
            <Spinner />
          </Center>
        ) : (
          <EditPatternForm
            experienceList={experienceList || []}
            refetch={refetch}
            closeDialog={() => setCreateDialogOpen(false)}
          />
        )}
      </ResponsiveDialog>

      <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-start py-4">
        <div className="flex flex-col lg:max-w-[60%]">
          <Typography variant="h1" className="text-2xl md:text-5xl" data-testid="patterns-dashboard-title">
            {t('dashboard.patternsTitle')}
          </Typography>
          <Typography className="dark:text-gray-200">{t('dashboard.patternsDescription')}</Typography>
        </div>

        <div className="flex items-center space-x-4">
          <IconButton
            size="sm"
            className="rounded-full"
            onClick={() => setCreateDialogOpen(true)}
            data-testid="create-pattern-btn"
          >
            <AiOutlinePlus className="text-[20px]" />
          </IconButton>
          <Typography className="font-bold dark:text-gray-200">{t('dashboard.createPattern')}</Typography>
        </div>
      </div>

      <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {isLoading && <Spinner className="h-6 w-6" />}

        {!isLoading && (!patternsList || patternsList.length === 0) && (
          <div className="w-full col-span-3 text-center">
            <NoUploads>
              <Typography className="dark:text-gray-200">{t('dashboard.noPatternsYet')}</Typography>
            </NoUploads>
          </div>
        )}

        {!isLoading &&
          patternsList?.map((pattern) => (
            <div key={pattern.pattern_id} className="flex-1 mb-4 basis-1/4">
              <PatternPreviewCard pattern={pattern} experienceList={experienceList || []} refetch={refetch} />
            </div>
          ))}
      </div>
    </>
  )
}

export default DashboardPatterns
